import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { formatPrice, getDefaultCurrency } from '../utils/currency';

export interface WishlistItem {
  id: number;
  user_id: number;
  house: string;
  name: string;
  concentration?: string;
  size_oz?: number;
  size_ml?: number;
  notes_top?: string;
  notes_middle?: string;
  notes_base?: string;
  price?: number;
  currency: string;
  retailer?: string;
  retailer_link?: string;
  notes?: string;
  is_gift_idea: boolean;
  priority: 'low' | 'medium' | 'high';
  created_at: string;
  updated_at: string;
}

interface WishlistFilters {
  search?: string;
  house?: string;
  priority?: 'low' | 'medium' | 'high';
  is_gift_idea?: boolean;
  min_price?: number;
  max_price?: number;
  currency?: string;
}

interface WishlistSorting {
  sort_by: string;
  sort_order: 'asc' | 'desc';
}

interface WishlistPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface WishlistCollection {
  wishlistItems: WishlistItem[];
  pagination: WishlistPagination;
}

interface WishlistStats {
  totals: {
    items: number;
    giftIdeas: number;
    houses: number;
  };
  pricesByCurrency: Array<{ currency: string; total_price: number }>;
  priorityBreakdown: Array<{ priority: string; count: number }>;
  topHouses: Array<{ house: string; count: number }>;
}

interface WishlistContextType {
  // Data
  collection: WishlistCollection | null;
  stats: WishlistStats | null;
  isLoading: boolean;
  error: string | null;
  
  // Filters and sorting
  filters: WishlistFilters;
  sorting: WishlistSorting;
  pageSize: number;
  
  // Actions
  fetchWishlistItems: (filters?: WishlistFilters, sorting?: WishlistSorting, page?: number, pageSize?: number) => Promise<void>;
  createWishlistItem: (itemData: Partial<WishlistItem>) => Promise<{ success: boolean; message: string; wishlistItem?: WishlistItem }>;
  updateWishlistItem: (id: number, itemData: Partial<WishlistItem>) => Promise<{ success: boolean; message: string; wishlistItem?: WishlistItem }>;
  deleteWishlistItem: (id: number) => Promise<{ success: boolean; message: string }>;
  moveToCollection: (id: number) => Promise<{ success: boolean; message: string }>;
  fetchStats: () => Promise<void>;
  
  // Filter and sort management
  setFilters: (filters: WishlistFilters) => void;
  setSorting: (sorting: WishlistSorting) => void;
  setPageSize: (size: number) => void;
  clearFilters: () => void;
  
  // Utility
  copyWishlistDetails: (item: WishlistItem) => void;
  refreshWishlist: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

interface WishlistProviderProps {
  children: React.ReactNode;
}

// Create axios instance for API calls
const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3001/api`;
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

export const WishlistProvider: React.FC<WishlistProviderProps> = ({ children }) => {
  const [collection, setCollection] = useState<WishlistCollection | null>(null);
  const [stats, setStats] = useState<WishlistStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<WishlistFilters>({});
  const [sorting, setSorting] = useState<WishlistSorting>({ sort_by: 'priority', sort_order: 'desc' });
  const [pageSize, setPageSize] = useState<number>(() => {
    const saved = localStorage.getItem('fragrance-organizer-wishlist-page-size');
    return saved ? parseInt(saved) : 50;
  });

  // Set up axios interceptor for auth token
  React.useEffect(() => {
    const token = localStorage.getItem('fragrance-organizer-token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  const handleFilters = useCallback((newFilters: WishlistFilters) => {
    setFilters(newFilters);
  }, []);

  const handleSorting = useCallback((newSorting: WishlistSorting) => {
    setSorting(newSorting);
  }, []);

  const handlePageSize = useCallback((size: number) => {
    setPageSize(size);
    localStorage.setItem('fragrance-organizer-wishlist-page-size', size.toString());
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      setError(null);
      const response = await api.get('/wishlist/stats');

      if (response.data.success) {
        setStats(response.data.data.stats);
      } else {
        setError(response.data.message || 'Failed to fetch statistics');
      }
    } catch (error: any) {
      console.error('Fetch wishlist stats error:', error);
      setError(error.response?.data?.message || 'Failed to fetch statistics');
    }
  }, []);

  const fetchWishlistItems = useCallback(async (
    customFilters?: WishlistFilters,
    customSorting?: WishlistSorting,
    page: number = 1,
    customPageSize?: number
  ) => {
    try {
      setIsLoading(true);
      setError(null);

      const activeFilters = customFilters || filters;
      const activeSorting = customSorting || sorting;
      const activePageSize = customPageSize !== undefined ? customPageSize : pageSize;

      const params = new URLSearchParams({
        page: page.toString(),
        limit: activePageSize.toString(),
        sort_by: activeSorting.sort_by,
        sort_order: activeSorting.sort_order,
      });

      // Add filters to params
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });

      const response = await api.get(`/wishlist?${params.toString()}`);

      if (response.data.success) {
        setCollection(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch wishlist items');
      }
    } catch (error: any) {
      console.error('Fetch wishlist items error:', error);
      setError(error.response?.data?.message || 'Failed to fetch wishlist items');
    } finally {
      setIsLoading(false);
    }
  }, [filters, sorting, pageSize]);

  const createWishlistItem = useCallback(async (itemData: Partial<WishlistItem>) => {
    try {
      setError(null);
      const response = await api.post('/wishlist', itemData);

      if (response.data.success) {
        // Refresh the wishlist and stats
        await fetchWishlistItems();
        await fetchStats();
        
        return {
          success: true,
          message: response.data.message,
          wishlistItem: response.data.data.wishlistItem
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to create wishlist item'
        };
      }
    } catch (error: any) {
      console.error('Create wishlist item error:', error);
      const message = error.response?.data?.message || 'Failed to create wishlist item';
      setError(message);
      return {
        success: false,
        message
      };
    }
  }, [fetchWishlistItems, fetchStats]);

  const updateWishlistItem = useCallback(async (id: number, itemData: Partial<WishlistItem>) => {
    try {
      setError(null);
      const response = await api.put(`/wishlist/${id}`, itemData);

      if (response.data.success) {
        // Update the collection state
        if (collection) {
          const updatedItems = collection.wishlistItems.map(item =>
            item.id === id ? response.data.data.wishlistItem : item
          );
          setCollection({ ...collection, wishlistItems: updatedItems });
        }
        
        // Refresh stats in case price or other stats-affecting fields changed
        await fetchStats();
        
        return {
          success: true,
          message: response.data.message,
          wishlistItem: response.data.data.wishlistItem
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to update wishlist item'
        };
      }
    } catch (error: any) {
      console.error('Update wishlist item error:', error);
      const message = error.response?.data?.message || 'Failed to update wishlist item';
      setError(message);
      return {
        success: false,
        message
      };
    }
  }, [collection]);

  const deleteWishlistItem = useCallback(async (id: number) => {
    try {
      setError(null);
      const response = await api.delete(`/wishlist/${id}`);

      if (response.data.success) {
        // Update the collection state
        if (collection) {
          const updatedItems = collection.wishlistItems.filter(item => item.id !== id);
          setCollection({ ...collection, wishlistItems: updatedItems });
        }
        
        // Refresh stats to reflect the deletion
        await fetchStats();
        
        return {
          success: true,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to delete wishlist item'
        };
      }
    } catch (error: any) {
      console.error('Delete wishlist item error:', error);
      const message = error.response?.data?.message || 'Failed to delete wishlist item';
      setError(message);
      return {
        success: false,
        message
      };
    }
  }, [collection, fetchStats]);

  const moveToCollection = useCallback(async (id: number) => {
    try {
      setError(null);
      const response = await api.post(`/wishlist/${id}/move-to-collection`);

      if (response.data.success) {
        // Remove from wishlist and refresh both collection and stats
        await fetchWishlistItems();
        await fetchStats();
        
        return {
          success: true,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Failed to move item to collection'
        };
      }
    } catch (error: any) {
      console.error('Move to collection error:', error);
      const message = error.response?.data?.message || 'Failed to move item to collection';
      setError(message);
      return {
        success: false,
        message
      };
    }
  }, [fetchWishlistItems, fetchStats]);

  const copyWishlistDetails = useCallback((item: WishlistItem) => {
    const details = [
      `${item.name} by ${item.house}`,
      item.concentration ? `Concentration: ${item.concentration}` : '',
      item.size_oz || item.size_ml ? `Size: ${item.size_oz ? item.size_oz + ' oz' : ''}${item.size_oz && item.size_ml ? ' / ' : ''}${item.size_ml ? item.size_ml + ' ml' : ''}` : '',
      item.notes_top ? `Top Notes: ${item.notes_top}` : '',
      item.notes_middle ? `Middle Notes: ${item.notes_middle}` : '',
      item.notes_base ? `Base Notes: ${item.notes_base}` : '',
      item.price ? `Price: ${formatPrice(item.price, item.currency)}` : '',
      item.retailer ? `Retailer: ${item.retailer}` : '',
      item.retailer_link ? `Link: ${item.retailer_link}` : '',
      item.priority ? `Priority: ${item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}` : '',
      item.is_gift_idea ? 'Gift Idea: Yes' : '',
      item.notes ? `Notes: ${item.notes}` : ''
    ].filter(Boolean).join('\n');

    // Check if navigator.clipboard is available (HTTPS or localhost)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(details).then(() => {
        console.log('Wishlist item details copied to clipboard');
      }).catch(err => {
        console.error('Failed to copy to clipboard:', err);
      });
    } else {
      // Fallback for older browsers or HTTP
      try {
        const textArea = document.createElement('textarea');
        textArea.value = details;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
        console.log('Wishlist item details copied to clipboard (fallback)');
      } catch (err) {
        console.error('Failed to copy to clipboard (fallback):', err);
      }
    }
  }, []);

  const refreshWishlist = useCallback(async () => {
    await fetchWishlistItems();
  }, [fetchWishlistItems]);

  const value: WishlistContextType = {
    collection,
    stats,
    isLoading,
    error,
    filters,
    sorting,
    pageSize,
    fetchWishlistItems,
    createWishlistItem,
    updateWishlistItem,
    deleteWishlistItem,
    moveToCollection,
    fetchStats,
    setFilters: handleFilters,
    setSorting: handleSorting,
    setPageSize: handlePageSize,
    clearFilters,
    copyWishlistDetails,
    refreshWishlist
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};